<template>
  <div class="h-auto">
    <div class="w-full mx-0 sm:mx-2 h-auto flex flex-wrap relative mb-10">
      <div class="sm:flex-shrink-0 w-full max-w-[715px] mr-0 sm:mr-10 mt-6 h-auto">
        <div class="mb-4">
          <EntryOnedioVideo v-if="isOnedioVideo" :entry="currentPlayingVideo" :is-hero="true" @current-video-id="handleCurrentVideoId" />
        </div>
        <ArticleTitle />
        <ArticleTags v-if="hasHashTags && !isMobile" />
        <section v-if="!isMobile" class="meta flex flex-row items-center justify-between pt-2 pb-1.5">
          <ArticleAuthor>
            <ArticleDate slot="article_date" class="font-normal text-sm text-primary-200" />
          </ArticleAuthor>
        </section>
      </div>
      <aside class="block flex-shrink-0 mt-4" :class="{ 'w-[345px]': !isMobile, 'w-full': isMobile }">
        <NextVideoWrapper
          v-if="onedioVideos.length > 1"
          :videos="onedioVideos"
          :current-video-id="currentVideoId"
          class="mb-4"
          @selected-video="handleSelectedVideo"
        />
        <RecommendationWrapper v-if="recommendedVideos && reachedMax" :items="recommendedVideos" />
      </aside>
    </div>
    <div class="w-full z-2" :class="{ 'sticky bottom-5': !isMobile, 'absolute left-1/2 transform -translate-x-1/2 bottom-0': isMobile }">
      <div
        class="
          hero-bottom-button
          u-center
          text-white text-sm
          p-2.5
          text-center
          absolute
          -bottom-3.5
          left-1/2
          transform
          -translate-x-1/2
          w-44
          h-9
          rounded-3xl
        "
      >
        <p>{{ $t('news-page.article-continues') }}</p>
        <DownArrow class="ml-2 rotate-90" height="11.5px" />
      </div>
    </div>
    <div class="hero-bottom-line h-1 w-full" :class="{ '-mx-4 absolute bottom-0': isMobile }"></div>
  </div>
</template>

<script>
import { defineComponent, inject, computed, ref, useFetch, useContext, onBeforeUnmount } from '@nuxtjs/composition-api';
import DownArrow from '@/static/svg/right-white.svg?inline';
import { shuffle } from 'lodash';
import NextVideoWrapper from './NextVideoWrapper';
import RecommendationWrapper from './RecommendationWrapper';
import { hasOnedioVideo } from '~/modules/article/utils/articleUtils';
import ArticleTitle from '~/modules/article/components/ArticleTitle';
import EntryOnedioVideo from '~/modules/video/component/EntryOnedioVideo';
import ArticleTags from '~/modules/article/components/ArticleTags';
import ArticleAuthor from '~/modules/article/components/ArticleAuthor';
import ArticleDate from '~/modules/article/components/ArticleDate';
import { useDevice } from '~/composables/useDevice';
import { eventBus } from '~/modules/common/utils/eventBus';

export default defineComponent({
  components: {
    EntryOnedioVideo,
    ArticleTitle,
    ArticleTags,
    ArticleAuthor,
    ArticleDate,
    NextVideoWrapper,
    RecommendationWrapper,
    DownArrow,
  },
  setup() {
    const article = inject('article');
    const { isMobile } = useDevice();
    const TOTAL_VIDEO = 7;
    let onedioVideos = [];
    let currentPlayingNumber = 0;
    const currentVideoId = ref('');
    const currentPlayingVideo = ref();
    const recommendedVideos = ref([]);
    const { $service } = useContext();
    const isOnedioVideo = computed(() => hasOnedioVideo(article));

    if (isOnedioVideo.value) {
      onedioVideos = article.entries
        .filter((entry) => {
          return entry.videoData?.source === 'onedio' && entry;
        })
        .map((v, i) => {
          // Sırayla play olan videolarda 2'den fazla reklam gösterilmesin
          if (i > 1) v.metadata = { ...v.metadata, disable_ads: true };
          return v;
        });

      // TEST CODE LINE
      // onedioVideos = [onedioVideos[0], ...Array(7).fill(onedioVideos[1])];
    }

    useFetch(async () => {
      if (process.client) return;
      const fethcedData = await $service.article.getRecommendedArticle(article.legacyId);
      const data = shuffle(fethcedData).slice(0, isMobile.value ? 5 : 7);
      if (onedioVideos.length === 1 || onedioVideos.length === 0 || isMobile.value) {
        recommendedVideos.value = data.slice(0, TOTAL_VIDEO) || null;
      } else {
        recommendedVideos.value = data.slice(0, TOTAL_VIDEO - onedioVideos.length) || null;
      }
    });

    // Video playlist
    eventBus.on('video-ended', () => {
      if (onedioVideos.length - 1 > currentPlayingNumber) {
        currentPlayingNumber++;
        currentPlayingVideo.value = onedioVideos[currentPlayingNumber];
      }
      // trigger to NextVideoItem.vue
      eventBus.emit('article-video-ended', currentPlayingNumber, currentPlayingVideo.value);
    });

    onBeforeUnmount(() => {
      eventBus.off('video-ended');
    });

    const reachedMax = computed(() => {
      return !(onedioVideos.length > 5);
    });

    currentPlayingVideo.value = onedioVideos[currentPlayingNumber];

    const handleCurrentVideoId = (id) => {
      currentVideoId.value = id;
    };

    const handleSelectedVideo = ({ video, index }) => {
      currentPlayingVideo.value = video;
      currentPlayingNumber = index;
      // sadece son videoda datalayer gönder
      if (onedioVideos.length === currentPlayingNumber + 1) {
        window.shouldSendRecommendationDatalayers = true;
      } else {
        window.shouldSendRecommendationDatalayers = false;
      }
    };

    const hasHashTags = computed(() => article?.hashtags.length > 0);

    return {
      onedioVideos,
      isOnedioVideo,
      article,
      hasHashTags,
      isMobile,
      reachedMax,
      currentVideoId,
      recommendedVideos,
      currentPlayingVideo,
      handleSelectedVideo,
      handleCurrentVideoId,
    };
  },
});
</script>
